import { AddressDialog } from '@/components/AddressDialog';
import { PermissionGuard } from '@/components/PermissionGuard';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useGetAddresses } from '@/hooks/useAddress';
import { useCheckout, useCheckoutItemCount } from '@/hooks/useCheckout';
import { useConfirmCheckout, useRemoveItemFromCheckout } from '@/hooks/useCheckoutActions';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useUserOrganizationRole } from '@/hooks/useUserRole';
import { getUserRole } from '@/lib/auth/token';
import { canWrite } from '@/lib/roleMapping';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CreditCard,
  MapPin,
  Package,
  Plus,
  ShoppingCart,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { formatPrice, formatDateShort } from '@/lib/format';

export function CheckoutSheet() {
  const { data: organization } = useCurrentOrganization();
  const userRole = getUserRole();
  const userOrganizationRole = useUserOrganizationRole();
  const canAccessCheckout = canWrite(userRole, userOrganizationRole);

  const [currentStep, setCurrentStep] = useState<
    'cart' | 'shipping' | 'address'
  >('cart');
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null
  );
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<
    'LETTRE_SIMPLE' | 'TNT' | null
  >(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { data: itemCount = 0 } = useCheckoutItemCount(organization?.id);
  const { data: checkout } = useCheckout(organization?.id, {
    enabled: isSheetOpen,
  });
  const { data: addresses } = useGetAddresses(
    isSheetOpen ? organization?.id : undefined,
  );
  const { mutate: removeItemFromCheckout } = useRemoveItemFromCheckout();
  const { mutate: confirmCheckout } = useConfirmCheckout();

  if (!organization || !canAccessCheckout) return null;

  const hasItems = itemCount > 0;
  const hasWeight = (checkout?.totalWeight ?? 0) > 0;

  const handleOrderConfirmation = () => {
    if (!hasWeight) {
      if (!checkout) return;
      confirmCheckout({
        checkoutId: checkout.id,
        deliveryAddress: null,
        shippingMethod: undefined,
      });
      return;
    }

    const selectedAddress = addresses?.find(
      (address) => address.id === selectedAddressId
    );
    if (!selectedAddress || !checkout) return;

    confirmCheckout({
      checkoutId: checkout.id,
      deliveryAddress: {
        country: selectedAddress.country,
        street: selectedAddress.street,
        city: selectedAddress.city,
        zipCode: selectedAddress.zipCode,
        isPersonal: selectedAddress.isPersonal,
        companyName: selectedAddress.companyName,
        recipientName: selectedAddress.recipientName,
      },
      shippingMethod: selectedShippingMethod || undefined,
    });
  };

  // ── Summary block (reused across steps) ──
  const renderSummary = (extraCost = 0) => {
    if (!checkout) return null;
    return (
      <div className="border-t border-slate-100 pt-5 pb-3 space-y-2.5">
        {checkout.fixedTotal > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Total HT (à l'acte)</span>
            <span className="font-semibold text-slate-900">
              {formatPrice(checkout.fixedTotal)}
            </span>
          </div>
        )}
        {checkout.recurringTotal > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Total HT/mois (forfaits)</span>
            <span className="font-semibold text-slate-900">
              {formatPrice(checkout.recurringTotal)}
            </span>
          </div>
        )}
        {extraCost > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Frais de port</span>
            <span className="font-semibold text-slate-900">
              {formatPrice(extraCost)}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between pt-3 border-t border-slate-100">
          <span className="text-sm font-bold text-slate-900">Total</span>
          <span className="text-xl font-bold text-slate-900">
            {formatPrice(checkout.total + extraCost)}
          </span>
        </div>
      </div>
    );
  };

  // ── Cart step ──
  const renderCartStep = () => (
    <>
      <div className="flex-1 overflow-y-auto space-y-3">
        {checkout?.items.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between rounded-xl border border-slate-100 bg-white px-4 py-3 transition-all hover:shadow-sm"
          >
            <div className="flex flex-col gap-1.5 min-w-0 flex-1">
              <span className="text-sm font-semibold text-slate-900 truncate">
                {item.name}
              </span>

              {/* Device metadata */}
              {item.type === 'DEVICE' && item.metadata && (
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {item.metadata.deviceCapacity && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
                      {item.metadata.deviceCapacity}GB
                    </span>
                  )}
                  {item.metadata.deviceColor && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
                      {item.metadata.deviceColor}
                    </span>
                  )}
                  {item.metadata.siteName && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
                      {item.metadata.siteName}
                    </span>
                  )}
                </div>
              )}

              {/* Accessory metadata */}
              {item.type === 'ACCESSORY' && item.metadata && (
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {item.metadata.color && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
                      {item.metadata.color}
                    </span>
                  )}
                  {item.metadata.compatibleDeviceBrand && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700">
                      {item.metadata.compatibleDeviceBrand}{' '}
                      {item.metadata.compatibleDeviceModel}
                    </span>
                  )}
                  {item.metadata.siteName && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
                      {item.metadata.siteName}
                    </span>
                  )}
                </div>
              )}

              {/* Travel eSIM metadata */}
              {item.type === 'TRAVEL_ESIM' && item.metadata && (
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {item.metadata.orderKind === 'RECHARGE' && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-violet-50 text-violet-700">
                      Recharge
                    </span>
                  )}
                  {item.metadata.beneficiary && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700">
                      {item.metadata.beneficiary.name}
                      {item.metadata.beneficiary.lineNumber
                        ? ` · ${item.metadata.beneficiary.lineNumber}`
                        : ''}
                    </span>
                  )}
                  {item.metadata.departureDate && item.metadata.returnDate && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
                      {formatDateShort(item.metadata.departureDate)} au{' '}
                      {formatDateShort(item.metadata.returnDate)}
                    </span>
                  )}
                  {item.metadata.siteName && (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600">
                      {item.metadata.siteName}
                    </span>
                  )}
                </div>
              )}

              {/* SIM metadata */}
              {item.type === 'SIM' && item.metadata?.siteName && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-600 w-fit">
                  {item.metadata.siteName}
                </span>
              )}

              {/* LINE SIM metadata */}
              {item.type === 'LINE' &&
                item.metadata?.physiqueOption === 'stock' &&
                item.metadata?.simNumber && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-amber-50 text-amber-700 w-fit">
                    SIM: {item.metadata.simNumber}
                  </span>
                )}

              <span className="text-xs text-slate-500">
                {item.quantity} x {formatPrice(item.price)}
              </span>
            </div>

            <div className="flex items-center gap-3 shrink-0 ml-3">
              <span className="text-sm font-bold text-slate-900">
                {formatPrice(item.total)}
              </span>
              <PermissionGuard permission="write">
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        aria-label="Supprimer l'article"
                        onClick={() =>
                          item.isDeletable &&
                          removeItemFromCheckout({ itemId: item.id })
                        }
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                          item.isDeletable
                            ? 'text-slate-400 hover:bg-red-50 hover:text-red-500'
                            : 'text-slate-300 cursor-not-allowed'
                        )}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {item.isDeletable
                          ? 'Supprimer'
                          : 'Cette carte SIM est liée à une ligne et ne peut pas être supprimée séparément'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </PermissionGuard>
            </div>
          </div>
        ))}
      </div>

      {renderSummary()}

      <SheetFooter>
        <PermissionGuard permission="write">
          <Button
            className="w-full h-11 gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all"
            onClick={() => {
              if (hasWeight) {
                setCurrentStep('shipping');
              } else {
                handleOrderConfirmation();
              }
            }}
            disabled={!hasItems}
          >
            {hasWeight ? (
              <>
                Frais de port
                <ArrowRight className="h-4 w-4" />
              </>
            ) : (
              <>
                <CreditCard className="h-4 w-4" />
                Valider ma commande
              </>
            )}
          </Button>
        </PermissionGuard>
      </SheetFooter>
    </>
  );

  // ── Shipping step ──
  const renderShippingStep = () => {
    const availableMethods = checkout?.availableShippingMethods || [];
    const selectedMethod = availableMethods.find(
      (m) => m.method === selectedShippingMethod
    );

    return (
      <>
        <div className="flex-1 overflow-y-auto space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">
            Méthode de livraison
          </h3>

          {availableMethods.length > 0 ? (
            <div className="space-y-3">
              {availableMethods.map((method) => (
                <button
                  key={method.method}
                  onClick={() =>
                    setSelectedShippingMethod(
                      method.method as 'LETTRE_SIMPLE' | 'TNT'
                    )
                  }
                  className={cn(
                    'flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all',
                    selectedShippingMethod === method.method
                      ? 'border-emerald-200 bg-emerald-50'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 mt-0.5 transition-all',
                      selectedShippingMethod === method.method
                        ? 'border-emerald-500 bg-emerald-500'
                        : 'border-slate-300'
                    )}
                  >
                    {selectedShippingMethod === method.method && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900">
                      {method.label}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {method.description}
                    </p>
                  </div>
                  <span className="text-sm font-bold text-slate-900 shrink-0">
                    {formatPrice(method.cost)}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-12 text-slate-400">
              <Package className="h-10 w-10 mb-3 text-slate-300" />
              <p className="text-sm">Aucune méthode disponible</p>
            </div>
          )}
        </div>

        {renderSummary(selectedMethod?.cost ?? 0)}

        <SheetFooter className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 h-11 rounded-full border-slate-200 text-slate-600"
            onClick={() => setCurrentStep('cart')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <Button
            className="flex-1 h-11 gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all"
            disabled={!selectedShippingMethod}
            onClick={() => setCurrentStep('address')}
          >
            Adresse
            <ArrowRight className="h-4 w-4" />
          </Button>
        </SheetFooter>
      </>
    );
  };

  // ── Address step ──
  const renderAddressStep = () => (
    <>
      <div className="flex-1 overflow-y-auto space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">
            Adresse de livraison
          </h3>
          <PermissionGuard permission="write">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddressDialogOpen(true)}
              className="rounded-full bg-white border-slate-200 text-slate-600 gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Nouvelle
            </Button>
          </PermissionGuard>
        </div>

        {addresses && addresses.length > 0 ? (
          <RadioGroup
            value={selectedAddressId || ''}
            onValueChange={setSelectedAddressId}
            className="space-y-3"
          >
            {addresses.map((address) => (
              <label
                key={address.id}
                htmlFor={address.id}
                className={cn(
                  'flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-all',
                  selectedAddressId === address.id
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                )}
              >
                <RadioGroupItem
                  value={address.id}
                  id={address.id}
                  className="mt-0.5 shrink-0"
                />
                <div className="flex-1 min-w-0">
                  {!address.isPersonal && address.companyName && (
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                      {address.companyName}
                    </p>
                  )}
                  {address.recipientName && (
                    <p className="text-xs text-slate-500">
                      {address.recipientName}
                    </p>
                  )}
                  <p className="text-sm font-semibold text-slate-900 mt-0.5">
                    {address.name}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {address.street}
                  </p>
                  <p className="text-xs text-slate-400">
                    {address.zipCode} {address.city}, {address.country}
                  </p>
                </div>
                {selectedAddressId === address.id && (
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                )}
              </label>
            ))}
          </RadioGroup>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <MapPin className="h-10 w-10 mb-3 text-slate-300" />
            <p className="text-sm font-medium">Aucune adresse</p>
            <p className="text-xs mt-1">
              Ajoutez une adresse pour continuer
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAddressDialogOpen(true)}
              className="mt-4 rounded-full bg-white border-slate-200 text-slate-600 gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Ajouter une adresse
            </Button>
          </div>
        )}

      </div>

      {/* Personal address warning — outside scroll area so it's always visible */}
      {selectedAddressId &&
        addresses?.find((a) => a.id === selectedAddressId)?.isPersonal && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
            Une livraison vers cette adresse personnelle entraînera des frais
            supplémentaires de 3€
          </div>
        )}

      <AddressDialog
        open={isAddressDialogOpen}
        onOpenChange={setIsAddressDialogOpen}
      />

      <SheetFooter className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1 h-11 rounded-full border-slate-200 text-slate-600"
          onClick={() => setCurrentStep('cart')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>
        <Button
          className="flex-1 h-11 gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all"
          disabled={!selectedAddressId}
          onClick={handleOrderConfirmation}
        >
          <CreditCard className="h-4 w-4" />
          Valider ma commande
        </Button>
      </SheetFooter>
    </>
  );

  return (
    <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          aria-label="Ouvrir le panier"
          className="rounded-full bg-white border-slate-200 text-slate-600 gap-1.5"
        >
          <ShoppingCart className="h-4 w-4" />
          Panier
          {hasItems && (
            <span className="rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {itemCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="p-5 flex flex-col h-full bg-white border-l border-slate-100 shadow-2xl w-full sm:w-3/4 sm:max-w-2xl md:w-2/3 lg:w-1/2"
      >
        <SheetHeader className="pb-5">
          <SheetTitle className="text-lg font-bold text-slate-900">
            Votre commande
          </SheetTitle>
          <SheetDescription className="text-sm text-slate-500">
            Récapitulatif de votre commande
          </SheetDescription>
        </SheetHeader>

        {hasItems && !checkout ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400">
            <ShoppingCart className="h-10 w-10 mb-3 text-slate-300 animate-pulse" />
            <p className="text-sm">Chargement du panier…</p>
          </div>
        ) : hasItems && checkout ? (
          currentStep === 'cart' ? (
            renderCartStep()
          ) : currentStep === 'shipping' && hasWeight ? (
            renderShippingStep()
          ) : currentStep === 'address' && hasWeight ? (
            renderAddressStep()
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center">
              <Package className="h-10 w-10 mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-900">
                Commande sans livraison
              </p>
              <p className="text-xs text-slate-400 mt-1">
                Pas d'adresse requise
              </p>
              <Button
                className="mt-4 gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all"
                onClick={handleOrderConfirmation}
              >
                <CreditCard className="h-4 w-4" />
                Valider ma commande
              </Button>
            </div>
          )
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <ShoppingCart className="h-10 w-10 mb-3 text-slate-300" />
            <p className="text-sm font-medium text-slate-900">
              Votre panier est vide
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Ajoutez des articles pour commencer
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
